import "./AnthropometryDetailedProgress.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import ChevronBottomIcon from "../../assets/imgs/chevron-bottom-icon.svg";
import ChevronRightIcon from "../../assets/imgs/chevron-right-icon.svg";
import DefaultButton from "../../components/DefaultButton/DefaultButton";
import loginService from "../../services/login-service";
import measurementService from "../../services/measurement-service";
import userService from "../../services/user-service";
import { GlobalContext } from "../GlobalContext";
import CompositionDonutPanel from "./CompositionDonutPanel";
import MuscleComparisonPanel from "./MuscleComparisonPanel";
import MuscleGroupAnalysisPanel from "./MuscleGroupAnalysisPanel";
import MuscleRegionEvolutionPanel from "./MuscleRegionEvolutionPanel";
import {
  filterCompletedMeasurements,
  filterMeasurementsByDateRange,
  getAvailableVisitDateOptions,
  getCompositionVisitOptions,
  getEndDateOptions,
  getMeasurementDateBounds,
  getStartDateOptions,
  sortMeasurementsTimeline,
} from "./detailed-progress-utils";
import MuscleDetailDateRange from "./MuscleDetailDateRange";
import MuscleDetailFilterDropdown from "./MuscleDetailFilterDropdown";
import {
  buildMuscleDetailRows,
  buildSkinfoldDetailRows,
  DEFAULT_VISIBLE_MUSCLE_ROWS,
  filterMuscleDetailRows,
  getMuscleDetailFilterOptions,
  getMuscleEvolutionHighlights,
  sortMuscleDetailRowsByVariation,
} from "./muscle-area-utils";
import MuscleEvolutionSparkline from "./MuscleEvolutionSparkline";
import ProgressMonthlySection from "./ProgressMonthlySection";

const DetailRegionsTableCard = ({
  title,
  subtitle,
  regionColumnLabel,
  startOptions,
  endOptions,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  filterOptions,
  selectedRegions,
  onRegionsChange,
  onClearRegions,
  searchPlaceholder,
  emptySearchMessage,
  filteredRows,
  visibleRows,
  hasHiddenRows,
  showAllRows,
  onShowAllRows,
  onShowLess,
  onClearFiltersAndPeriod,
  tableClassName,
  variationSortDirection,
  onVariationSortClick,
  expandedRowKey,
  onRowToggle,
}) => (
  <section className="anthropometry-detailed-progress__card anthropometry-detailed-progress__card--table">
    <div className="anthropometry-detailed-progress__card__header">
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>

      <div className="anthropometry-detailed-progress__toolbar">
        <MuscleDetailDateRange
          startOptions={startOptions}
          endOptions={endOptions}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
        />

        <MuscleDetailFilterDropdown
          options={filterOptions}
          selectedRegions={selectedRegions}
          onChange={onRegionsChange}
          onClear={onClearRegions}
          searchPlaceholder={searchPlaceholder}
          emptySearchMessage={emptySearchMessage}
        />
      </div>
    </div>

    {filteredRows.length > 0 ? (
      <>
        <div className="anthropometry-detailed-progress__table-wrapper">
          <table className={`anthropometry-detailed-progress__table${tableClassName ? ` ${tableClassName}` : ""}`}>
            <thead>
              <tr>
                <th>{regionColumnLabel}</th>
                <th>Último valor</th>
                <th>
                  {onVariationSortClick ? (
                    <button
                      type="button"
                      className={`anthropometry-detailed-progress__table__sort-button${
                        variationSortDirection
                          ? " anthropometry-detailed-progress__table__sort-button--active"
                          : ""
                      }`}
                      onClick={onVariationSortClick}
                      aria-sort={
                        variationSortDirection === "asc"
                          ? "ascending"
                          : variationSortDirection === "desc"
                            ? "descending"
                            : "none"
                      }
                    >
                      Variação
                      {variationSortDirection === "asc" && (
                        <span aria-hidden="true"> ↑</span>
                      )}
                      {variationSortDirection === "desc" && (
                        <span aria-hidden="true"> ↓</span>
                      )}
                    </button>
                  ) : (
                    "Variação"
                  )}
                </th>
                <th>Evolução</th>
                <th aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const isExpanded = onRowToggle && expandedRowKey === row.key;

                return (
                  <React.Fragment key={row.key}>
                    <tr
                      className={
                        onRowToggle
                          ? `anthropometry-detailed-progress__table__row--clickable${
                              isExpanded
                                ? " anthropometry-detailed-progress__table__row--expanded"
                                : ""
                            }`
                          : undefined
                      }
                      role={onRowToggle ? "button" : undefined}
                      tabIndex={onRowToggle ? 0 : undefined}
                      aria-expanded={onRowToggle ? isExpanded : undefined}
                      aria-label={
                        onRowToggle
                          ? `${isExpanded ? "Recolher" : "Expandir"} evolução de ${row.regionLabel}`
                          : undefined
                      }
                      onClick={
                        onRowToggle ? () => onRowToggle(row) : undefined
                      }
                      onKeyDown={
                        onRowToggle
                          ? (event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                onRowToggle(row);
                              }
                            }
                          : undefined
                      }
                    >
                      <td className="anthropometry-detailed-progress__table__region">
                        {row.regionLabel}
                      </td>
                      <td className="anthropometry-detailed-progress__table__value">
                        {row.lastValueLabel}
                      </td>
                      <td>
                        <span
                          className={`anthropometry-detailed-progress__variation anthropometry-detailed-progress__variation--${row.trend}`}
                        >
                          {row.variationLabel}
                        </span>
                      </td>
                      <td className="anthropometry-detailed-progress__table__evolution">
                        <MuscleEvolutionSparkline
                          timeline={row.timeline}
                          trend={row.trend}
                          progressTone={row.progressTone}
                        />
                      </td>
                      <td className="anthropometry-detailed-progress__table__chevron">
                        {onRowToggle ? (
                          <img
                            src={ChevronRightIcon}
                            alt=""
                            className={
                              isExpanded
                                ? "anthropometry-detailed-progress__table__chevron-icon--expanded"
                                : undefined
                            }
                          />
                        ) : null}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="anthropometry-detailed-progress__table__row--expanded-content">
                        <td colSpan={5}>
                          <MuscleRegionEvolutionPanel row={row} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {hasHiddenRows && (
          <button
            type="button"
            className="anthropometry-detailed-progress__show-all"
            onClick={onShowAllRows}
          >
            Ver todos os {filteredRows.length} itens
            <img src={ChevronBottomIcon} alt="" />
          </button>
        )}

        {showAllRows && filteredRows.length > DEFAULT_VISIBLE_MUSCLE_ROWS && (
          <button
            type="button"
            className="anthropometry-detailed-progress__show-all"
            onClick={onShowLess}
          >
            Mostrar menos
            <img
              src={ChevronBottomIcon}
              alt=""
              className="anthropometry-detailed-progress__show-all__icon--up"
            />
          </button>
        )}
      </>
    ) : (
      <div className="anthropometry-detailed-progress__no-results">
        <p>Nenhuma medida encontrada com os filtros ou o período selecionado.</p>
        <button
          type="button"
          className="anthropometry-detailed-progress__clear-search"
          onClick={onClearFiltersAndPeriod}
        >
          Limpar filtros e período
        </button>
      </div>
    )}
  </section>
);

const AnthropometryDetailedProgress = () => {
  const { id: patientId } = useParams();
  const { setSelectedScreen, setIsLoading } = useContext(GlobalContext);

  const [patient, setPatient] = useState();
  const [measurements, setMeasurements] = useState([]);
  const [formError, setFormError] = useState("");

  const [selectedRegions, setSelectedRegions] = useState([]);
  const [showAllRows, setShowAllRows] = useState(false);
  const [muscleVariationSortDirection, setMuscleVariationSortDirection] = useState(null);
  const [expandedMuscleRowKey, setExpandedMuscleRowKey] = useState(null);
  const [tableStartDate, setTableStartDate] = useState("");
  const [tableEndDate, setTableEndDate] = useState("");

  const [selectedSkinfoldRegions, setSelectedSkinfoldRegions] = useState([]);
  const [showAllSkinfoldRows, setShowAllSkinfoldRows] = useState(false);
  const [skinfoldVariationSortDirection, setSkinfoldVariationSortDirection] = useState(null);
  const [expandedSkinfoldRowKey, setExpandedSkinfoldRowKey] = useState(null);
  const [skinfoldStartDate, setSkinfoldStartDate] = useState("");
  const [skinfoldEndDate, setSkinfoldEndDate] = useState("");

  const [selectedCompositionDate, setSelectedCompositionDate] = useState("");

  useEffect(() => {
    setSelectedScreen("patients");
  }, [setSelectedScreen]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setFormError("");
      try {
        await loginService.getCurrentUser();
        const [patientResponse, measurementsResponse] = await Promise.all([
          userService.getUserById(patientId),
          measurementService.listByPatient(patientId),
        ]);
        setPatient(patientResponse);
        setMeasurements(measurementsResponse || []);
      } catch (error) {
        console.error(error);
        setFormError("Não foi possível carregar o progresso detalhado.");
      } finally {
        setIsLoading(false);
      }
    };

    if (patientId) {
      fetchData();
    }
  }, [patientId, setIsLoading]);

  const timeline = useMemo(
    () => sortMeasurementsTimeline(measurements),
    [measurements]
  );

  const tableTimeline = useMemo(
    () => filterCompletedMeasurements(timeline),
    [timeline]
  );

  const tableDateBounds = useMemo(
    () => getMeasurementDateBounds(tableTimeline),
    [tableTimeline]
  );

  const visitDateOptions = useMemo(
    () => getAvailableVisitDateOptions(tableTimeline),
    [tableTimeline]
  );

  const compositionVisitOptions = useMemo(
    () => getCompositionVisitOptions(tableTimeline),
    [tableTimeline]
  );

  const startDateOptions = useMemo(
    () => getStartDateOptions(visitDateOptions, tableEndDate),
    [visitDateOptions, tableEndDate]
  );

  const endDateOptions = useMemo(
    () => getEndDateOptions(visitDateOptions, tableStartDate),
    [visitDateOptions, tableStartDate]
  );

  const skinfoldStartDateOptions = useMemo(
    () => getStartDateOptions(visitDateOptions, skinfoldEndDate),
    [visitDateOptions, skinfoldEndDate]
  );

  const skinfoldEndDateOptions = useMemo(
    () => getEndDateOptions(visitDateOptions, skinfoldStartDate),
    [visitDateOptions, skinfoldStartDate]
  );

  useEffect(() => {
    setTableStartDate(tableDateBounds.startDate);
    setTableEndDate(tableDateBounds.endDate);
    setSkinfoldStartDate(tableDateBounds.startDate);
    setSkinfoldEndDate(tableDateBounds.endDate);
  }, [tableDateBounds.startDate, tableDateBounds.endDate]);

  useEffect(() => {
    if (compositionVisitOptions.length === 0) {
      setSelectedCompositionDate("");
      return;
    }

    const isCurrentValid = compositionVisitOptions.some(
      (option) => option.value === selectedCompositionDate
    );

    if (!isCurrentValid) {
      setSelectedCompositionDate(
        compositionVisitOptions[compositionVisitOptions.length - 1].value
      );
    }
  }, [compositionVisitOptions, selectedCompositionDate]);

  const tableMeasurements = useMemo(
    () =>
      filterMeasurementsByDateRange(tableTimeline, tableStartDate, tableEndDate),
    [tableTimeline, tableStartDate, tableEndDate]
  );

  const skinfoldTableMeasurements = useMemo(
    () =>
      filterMeasurementsByDateRange(
        tableTimeline,
        skinfoldStartDate,
        skinfoldEndDate
      ),
    [tableTimeline, skinfoldStartDate, skinfoldEndDate]
  );

  const hasMuscleData = useMemo(
    () => buildMuscleDetailRows(tableTimeline).length > 0,
    [tableTimeline]
  );

  const hasSkinfoldData = useMemo(
    () => buildSkinfoldDetailRows(tableTimeline).length > 0,
    [tableTimeline]
  );

  const hasTableData = hasMuscleData || hasSkinfoldData;
  const hasCompositionData = compositionVisitOptions.length > 0;

  const muscleRows = useMemo(
    () => buildMuscleDetailRows(tableMeasurements),
    [tableMeasurements]
  );

  const muscleEvolutionHighlights = useMemo(
    () => getMuscleEvolutionHighlights(muscleRows),
    [muscleRows]
  );

  const filterOptions = useMemo(
    () => getMuscleDetailFilterOptions(muscleRows),
    [muscleRows]
  );

  const filteredRows = useMemo(
    () =>
      filterMuscleDetailRows(muscleRows, {
        regions: selectedRegions,
      }),
    [muscleRows, selectedRegions]
  );

  const sortedFilteredRows = useMemo(() => {
    if (!muscleVariationSortDirection) {
      return filteredRows;
    }

    return sortMuscleDetailRowsByVariation(
      filteredRows,
      muscleVariationSortDirection
    );
  }, [filteredRows, muscleVariationSortDirection]);

  const visibleRows = showAllRows
    ? sortedFilteredRows
    : sortedFilteredRows.slice(0, DEFAULT_VISIBLE_MUSCLE_ROWS);

  const hasHiddenRows =
    sortedFilteredRows.length > DEFAULT_VISIBLE_MUSCLE_ROWS && !showAllRows;

  const skinfoldRows = useMemo(
    () => buildSkinfoldDetailRows(skinfoldTableMeasurements),
    [skinfoldTableMeasurements]
  );

  const skinfoldFilterOptions = useMemo(
    () => getMuscleDetailFilterOptions(skinfoldRows),
    [skinfoldRows]
  );

  const filteredSkinfoldRows = useMemo(
    () =>
      filterMuscleDetailRows(skinfoldRows, {
        regions: selectedSkinfoldRegions,
      }),
    [skinfoldRows, selectedSkinfoldRegions]
  );

  const sortedFilteredSkinfoldRows = useMemo(() => {
    if (!skinfoldVariationSortDirection) {
      return filteredSkinfoldRows;
    }

    return sortMuscleDetailRowsByVariation(
      filteredSkinfoldRows,
      skinfoldVariationSortDirection
    );
  }, [filteredSkinfoldRows, skinfoldVariationSortDirection]);

  const visibleSkinfoldRows = showAllSkinfoldRows
    ? sortedFilteredSkinfoldRows
    : sortedFilteredSkinfoldRows.slice(0, DEFAULT_VISIBLE_MUSCLE_ROWS);

  const hasHiddenSkinfoldRows =
    sortedFilteredSkinfoldRows.length > DEFAULT_VISIBLE_MUSCLE_ROWS &&
    !showAllSkinfoldRows;

  const handleRegionsChange = (nextRegions) => {
    setSelectedRegions(nextRegions);
    setShowAllRows(false);
  };

  const handleClearRegions = () => {
    setSelectedRegions([]);
    setShowAllRows(false);
  };

  const handleMuscleVariationSortClick = () => {
    setMuscleVariationSortDirection((currentDirection) => {
      if (currentDirection === "desc") {
        return "asc";
      }
      return "desc";
    });
    setShowAllRows(false);
  };

  const handleMuscleRowToggle = (row) => {
    setExpandedMuscleRowKey((currentKey) =>
      currentKey === row.key ? null : row.key
    );
  };

  const handleSkinfoldRowToggle = (row) => {
    setExpandedSkinfoldRowKey((currentKey) =>
      currentKey === row.key ? null : row.key
    );
  };

  const handleTableStartDateChange = (nextStartDate) => {
    setTableStartDate(nextStartDate);
    if (tableEndDate && nextStartDate > tableEndDate) {
      setTableEndDate(nextStartDate);
    }
    setShowAllRows(false);
  };

  const handleTableEndDateChange = (nextEndDate) => {
    setTableEndDate(nextEndDate);
    if (tableStartDate && nextEndDate < tableStartDate) {
      setTableStartDate(nextEndDate);
    }
    setShowAllRows(false);
  };

  const handleSkinfoldRegionsChange = (nextRegions) => {
    setSelectedSkinfoldRegions(nextRegions);
    setShowAllSkinfoldRows(false);
  };

  const handleClearSkinfoldRegions = () => {
    setSelectedSkinfoldRegions([]);
    setShowAllSkinfoldRows(false);
  };

  const handleSkinfoldVariationSortClick = () => {
    setSkinfoldVariationSortDirection((currentDirection) => {
      if (currentDirection === "desc") {
        return "asc";
      }
      return "desc";
    });
    setShowAllSkinfoldRows(false);
  };

  const handleSkinfoldStartDateChange = (nextStartDate) => {
    setSkinfoldStartDate(nextStartDate);
    if (skinfoldEndDate && nextStartDate > skinfoldEndDate) {
      setSkinfoldEndDate(nextStartDate);
    }
    setShowAllSkinfoldRows(false);
  };

  const handleSkinfoldEndDateChange = (nextEndDate) => {
    setSkinfoldEndDate(nextEndDate);
    if (skinfoldStartDate && nextEndDate < skinfoldStartDate) {
      setSkinfoldStartDate(nextEndDate);
    }
    setShowAllSkinfoldRows(false);
  };

  const emptyMessage =
    "Ainda não há circunferências ou dobras cutâneas registradas para exibir a evolução.";

  const hasAnyData = timeline.length > 0;
  const monthlyMeasurements = hasMuscleData
    ? tableMeasurements
    : skinfoldTableMeasurements;

  return (
    <div className="anthropometry-detailed-progress">
      <div className="anthropometry-detailed-progress__top">
        <div className="anthropometry-detailed-progress__breadcrumb">
          <a href="/patients" className="anthropometry-detailed-progress__breadcrumb__link">
            Pacientes
          </a>
          <div className="anthropometry-detailed-progress__breadcrumb__divisor">
            <img src={ChevronRightIcon} alt="" />
          </div>
          <a
            href={`/patients/${patientId}`}
            className="anthropometry-detailed-progress__breadcrumb__link"
          >
            {patient?.name || "Paciente"}
          </a>
          <div className="anthropometry-detailed-progress__breadcrumb__divisor">
            <img src={ChevronRightIcon} alt="" />
          </div>
          <a
            href={`/patients/${patientId}/anthropometry`}
            className="anthropometry-detailed-progress__breadcrumb__link"
          >
            Antropometria
          </a>
          <div className="anthropometry-detailed-progress__breadcrumb__divisor">
            <img src={ChevronRightIcon} alt="" />
          </div>
          <p className="anthropometry-detailed-progress__breadcrumb__current">
            Progresso detalhado
          </p>
        </div>

        <DefaultButton
          title="Voltar para antropometria"
          type="secondary"
          onClick={() => {
            window.location.href = `/patients/${patientId}/anthropometry`;
          }}
          style="anthropometry-detailed-progress__back-button"
          size="small"
        />
      </div>

      <div className="anthropometry-detailed-progress__header">
        <h1>Progresso detalhado</h1>
        <p className="anthropometry-detailed-progress__subtitle">
          Acompanhamento da composição corporal e evolução regional
        </p>
      </div>

      {hasAnyData ? (
        <div className="anthropometry-detailed-progress__layout">
          <aside className="anthropometry-detailed-progress__column anthropometry-detailed-progress__column--left">
            {hasCompositionData ? (
              <CompositionDonutPanel
                measurements={tableTimeline}
                visitOptions={compositionVisitOptions}
                selectedVisitDate={selectedCompositionDate}
                onVisitDateChange={setSelectedCompositionDate}
              />
            ) : (
              <section className="anthropometry-detailed-progress__card anthropometry-detailed-progress__card--donut">
                <div className="anthropometry-detailed-progress__panel-empty">
                  <p>
                    Ainda não há massa magra e gordura registradas para exibir o gráfico.
                  </p>
                </div>
              </section>
            )}

            {hasMuscleData && (
              <MuscleGroupAnalysisPanel highlights={muscleEvolutionHighlights} />
            )}
          </aside>

          <div className="anthropometry-detailed-progress__column anthropometry-detailed-progress__column--right">
            {hasTableData ? (
              <>
                {hasMuscleData && (
                  <DetailRegionsTableCard
                    title="Músculos em detalhe"
                    subtitle="Evolução individual de cada região muscular"
                    regionColumnLabel="Área muscular"
                    startOptions={startDateOptions}
                    endOptions={endDateOptions}
                    startDate={tableStartDate}
                    endDate={tableEndDate}
                    onStartDateChange={handleTableStartDateChange}
                    onEndDateChange={handleTableEndDateChange}
                    filterOptions={filterOptions}
                    selectedRegions={selectedRegions}
                    onRegionsChange={handleRegionsChange}
                    onClearRegions={handleClearRegions}
                    searchPlaceholder="Buscar área muscular"
                    emptySearchMessage="Nenhuma área encontrada"
                    filteredRows={sortedFilteredRows}
                    visibleRows={visibleRows}
                    hasHiddenRows={hasHiddenRows}
                    showAllRows={showAllRows}
                    onShowAllRows={() => setShowAllRows(true)}
                    onShowLess={() => setShowAllRows(false)}
                    onClearFiltersAndPeriod={() => {
                      setSelectedRegions([]);
                      setTableStartDate(tableDateBounds.startDate);
                      setTableEndDate(tableDateBounds.endDate);
                      setShowAllRows(false);
                      setMuscleVariationSortDirection(null);
                    }}
                    tableClassName="anthropometry-detailed-progress__table--muscles"
                    variationSortDirection={muscleVariationSortDirection}
                    onVariationSortClick={handleMuscleVariationSortClick}
                    expandedRowKey={expandedMuscleRowKey}
                    onRowToggle={handleMuscleRowToggle}
                  />
                )}

                {hasSkinfoldData && (
                  <DetailRegionsTableCard
                    title="Dobras em detalhe"
                    subtitle="Evolução individual de cada dobra cutânea"
                    regionColumnLabel="Dobra cutânea"
                    startOptions={skinfoldStartDateOptions}
                    endOptions={skinfoldEndDateOptions}
                    startDate={skinfoldStartDate}
                    endDate={skinfoldEndDate}
                    onStartDateChange={handleSkinfoldStartDateChange}
                    onEndDateChange={handleSkinfoldEndDateChange}
                    filterOptions={skinfoldFilterOptions}
                    selectedRegions={selectedSkinfoldRegions}
                    onRegionsChange={handleSkinfoldRegionsChange}
                    onClearRegions={handleClearSkinfoldRegions}
                    searchPlaceholder="Buscar dobra cutânea"
                    emptySearchMessage="Nenhuma dobra encontrada"
                    filteredRows={sortedFilteredSkinfoldRows}
                    visibleRows={visibleSkinfoldRows}
                    hasHiddenRows={hasHiddenSkinfoldRows}
                    showAllRows={showAllSkinfoldRows}
                    onShowAllRows={() => setShowAllSkinfoldRows(true)}
                    onShowLess={() => setShowAllSkinfoldRows(false)}
                    onClearFiltersAndPeriod={() => {
                      setSelectedSkinfoldRegions([]);
                      setSkinfoldStartDate(tableDateBounds.startDate);
                      setSkinfoldEndDate(tableDateBounds.endDate);
                      setShowAllSkinfoldRows(false);
                      setSkinfoldVariationSortDirection(null);
                    }}
                    tableClassName="anthropometry-detailed-progress__table--skinfolds"
                    variationSortDirection={skinfoldVariationSortDirection}
                    onVariationSortClick={handleSkinfoldVariationSortClick}
                    expandedRowKey={expandedSkinfoldRowKey}
                    onRowToggle={handleSkinfoldRowToggle}
                  />
                )}

                {hasMuscleData && (
                  <MuscleComparisonPanel
                    rows={muscleRows}
                    highlights={muscleEvolutionHighlights}
                  />
                )}

                <ProgressMonthlySection measurements={monthlyMeasurements} />
              </>
            ) : (
              <section className="anthropometry-detailed-progress__card anthropometry-detailed-progress__card--empty">
                <h2>Sem dados para mostrar</h2>
                <p>{emptyMessage}</p>
                <DefaultButton
                  title="Ir para antropometria"
                  onClick={() => {
                    window.location.href = `/patients/${patientId}/anthropometry`;
                  }}
                  size="small"
                />
              </section>
            )}
          </div>
        </div>
      ) : (
        <section className="anthropometry-detailed-progress__card anthropometry-detailed-progress__card--empty">
          <h2>Sem dados para mostrar</h2>
          <p>{emptyMessage}</p>
          <DefaultButton
            title="Ir para antropometria"
            onClick={() => {
              window.location.href = `/patients/${patientId}/anthropometry`;
            }}
            size="small"
          />
        </section>
      )}

      {formError && (
        <p className="anthropometry-detailed-progress__error">{formError}</p>
      )}
    </div>
  );
};

export default AnthropometryDetailedProgress;
